import { SingleOrderAuthError } from "@redotech/customer-portal-sdk/rpc/schema/single-order-auth";
import { LoadState, useTriggerLoad } from "@redotech/react-util/load";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { Step } from "@redotech/redo-model/return-flow";
import { ReturnOptionMethod } from "@redotech/redo-model/return-flow/return-option";
import { ClientError } from "@redotech/rpc/errors";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SetAuthContext } from "../contexts/auth";
import { SettingsContext } from "../contexts/settings";
import { SetUserContext } from "../contexts/user";
import {
  getHeaderText,
  getLoginGiftText,
  singleOrderAuthErrorToText,
} from "./dynamic-text-utils";

export interface UseSingleOrderLoginResponse {
  texts: {
    merchantName: string;
    title: string;
    subtitle: string;
    emailZipInputLabel: string;
    emailZipInputPlaceholder: string;
    orderNumberInputLabel: string;
    orderNumberInputPlaceholder: string;
    startReturnButton: string;
    giftButton: string;
  };

  emailZip: string;
  orderTrackingNumber: string;
  setEmailZip: (emailZip: string) => void;
  setOrderTrackingNumber: (orderTrackingNumber: string) => void;
  submit: () => void;
  submitLoad: LoadState<void | undefined>;
  submitDisabled: boolean;
  hideGiftOption: boolean;
}
export function useSingleOrderLogin(): UseSingleOrderLoginResponse {
  const settings = useContext(SettingsContext);
  const rpcClient = useContext(RpcClientContext);
  const setAuth_ = useContext(SetAuthContext);
  const setUser = useContext(SetUserContext);
  const navigate = useNavigate();

  const [emailZip, setEmailZip] = useState<string>("");
  const [orderTrackingNumber, setOrderTrackingNumber] = useState<string>("");

  const [submitLoad, triggerSubmitLoad] = useTriggerLoad(async () => {
    if (
      !emailZip ||
      !orderTrackingNumber ||
      !rpcClient ||
      !setAuth_ ||
      !setUser
    ) {
      return;
    }

    try {
      const result = await rpcClient.singleOrderAuth({
        emailOrZip: emailZip,
        orderIdTrackingNumber: orderTrackingNumber,
      });

      if (result.customerAuthToken && result.orderId) {
        setAuth_(result.customerAuthToken);
        setUser(result.customer ?? undefined);
        navigate(`/returns-portal/orders/${result.orderId}`, { replace: true });
      } else {
        throw new Error("Something went wrong on our end, please try again.");
      }
    } catch (e) {
      if (e instanceof ClientError) {
        const rpcError = e as ClientError<SingleOrderAuthError>;
        if (rpcError.code) {
          throw new Error(singleOrderAuthErrorToText[rpcError.code]);
        }
      }
      throw e;
    }
  });

  // If the merchant only offers returns for refund then we should hide the option for returning a gift,
  // since gift returns cannot be returned for refund.
  let hideGiftOption = true;
  if (
    settings?.packageProtection?.enabled &&
    settings.packageProtection.customerClaimsEnabled &&
    !settings.hideGiftOption
  ) {
    hideGiftOption = false;
  }
  if (settings?.returns?.enabled && hideGiftOption) {
    const returnOptionSteps = settings.returnFlow.steps.filter(
      (step) => step.type === Step.RETURN,
    );
    for (const returnOptionStep of returnOptionSteps) {
      for (const option of returnOptionStep.options) {
        if (option.method.type !== ReturnOptionMethod.REFUND) {
          hideGiftOption = false;
        }
      }
    }
  }

  return {
    texts: {
      merchantName: settings?.merchantName || "Unknown Merchant",
      title: getHeaderText(settings),
      subtitle: "Enter your email/zip and order or tracking number to login",
      emailZipInputLabel: "Email/Zip code",
      emailZipInputPlaceholder: "Enter email/zip...",
      orderNumberInputLabel: "Order# / Tracking#",
      orderNumberInputPlaceholder: "Enter order number...",
      startReturnButton: "Search",
      giftButton: getLoginGiftText(settings),
    },
    emailZip: emailZip || "",
    orderTrackingNumber: orderTrackingNumber || "",
    setEmailZip,
    setOrderTrackingNumber,
    submit: triggerSubmitLoad,
    submitLoad,
    submitDisabled: !emailZip || !orderTrackingNumber || submitLoad.pending,
    hideGiftOption,
  };
}
